/**
 * PATH: packages/ui/src/diagnostics/clientPerfReporter.ts
 * FILE_ID: SHARED-DIAGNOSTICS-PERF-001
 * PURPOSE: Capture frontend performance timings in milliseconds —
 *          component renders (useRenderTiming), fetch round-trips, Web
 *          Vitals (LCP/INP/CLS), and route transitions — and batch-ship
 *          them to /api/diagnostics/client-perf/ so the 0x3admin
 *          Performance dashboard can show "render p95 per UI element" and
 *          "did the last deploy regress it" across all tenants.
 *
 * WHY:
 *   The product ask: "millisecond measurements on rendering of each UI
 *   element and backend call … critical in feedback and review as we
 *   scale to prevent things breaking". This is the render/network half;
 *   the backend ServerTimingMiddleware is the server half. For fetch
 *   samples we also carry the server's own duration (parsed from the
 *   Server-Timing response header) so the dashboard can split network
 *   time from server time on the SAME call.
 *
 * DESIGN (deliberately mirrors clientErrorReporter):
 *   - Ring buffer + debounced batched POST + localStorage persistence +
 *     beforeunload flush + 429 backoff. Pure browser globals, no app deps.
 *   - We do NOT patch window.fetch here — clientErrorReporter already does
 *     and computes the duration for every call. It forwards timings to us
 *     via setFetchTimingObserver(), so there is exactly ONE fetch wrapper.
 *   - Render timings come from the useRenderTiming() hook (separate file
 *     so this module stays framework-agnostic).
 *
 * RELATED:
 *   - audit/views_client_perf.py (backend ingest + summary)
 *   - clientErrorReporter.ts (shares session_key + the fetch wrapper)
 */

import { getDiagnosticsSessionKey, setFetchTimingObserver } from './clientErrorReporter'

// --------- Types -------------------------------------------------------
// 'kpi' = a business / product milestone measured in ms (e.g.
// time_to_first_board_paint, time_to_first_action). Rides the SAME
// pipeline + ClientPerfSample store as technical timings so 0x3admin's
// Performance dashboard gets product KPIs as first-class metrics with
// zero new infrastructure (the backend ingest accepts the new kind).
export type PerfSampleKind = 'render' | 'fetch' | 'web_vital' | 'route' | 'kpi'

// PerformanceObserver entry shapes not fully typed in lib.dom.
interface LcpEntry extends PerformanceEntry {
  renderTime?: number
  loadTime?: number
}
interface LayoutShiftEntry extends PerformanceEntry {
  value: number
  hadRecentInput: boolean
  /** Attribution API (Chromium) — the nodes that moved in this shift. */
  sources?: Array<{ node?: Node | null }>
}
type ObserveInit = PerformanceObserverInit & { durationThreshold?: number }

export interface PerfSample {
  kind: PerfSampleKind
  /** What was measured: "EmailList", "EmailReader", "LCP", "GET /api/emails/". */
  name: string
  duration_ms: number
  /** Server-side portion for kind='fetch' (from the Server-Timing header). */
  server_duration_ms?: number
  route?: string
  extra?: Record<string, unknown>
}

interface QueuedSample extends PerfSample {
  ts: string
  session_key: string
  tenant: string
  build_sha?: string
}

export interface ClientPerfOptions {
  ingestUrl?: string
  buildSha?: string
  /** Tenant tag (e.g. "sam"). Derived from the hostname when omitted. */
  tenant?: string
  getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>
  getOrganisationId?: () => number | string | null | undefined
  /** Fraction of render/fetch samples kept [0..1]. 1.0 = full fidelity. */
  sampleRate?: number
  maxBufferSize?: number
  flushDebounceMs?: number
  disabled?: boolean
}

// --------- Module state -----------------------------------------------
const BUFFER_STORAGE_KEY = 'quotech_client_perf_buffer'

// Telemetry must not become a top poller. We flush on a long idle debounce
// (cheap, batched) and ONLY break that debounce early when a burst fills a
// full BE-sized batch — and even then no more often than MIN_BURST_FLUSH_MS,
// so a hot render/fetch loop can't turn the reporter into a POST storm.
const FLUSH_BATCH_SIZE = 100 // BE ingests up to 100 events / request
const MIN_BURST_FLUSH_MS = 5000

let installed = false
let opts: Required<Pick<ClientPerfOptions, 'ingestUrl' | 'tenant' | 'sampleRate' | 'maxBufferSize' | 'flushDebounceMs'>> &
  ClientPerfOptions = {
  ingestUrl: '',
  tenant: '',
  sampleRate: 1.0,
  maxBufferSize: 200,
  flushDebounceMs: 30000,
}
let buffer: QueuedSample[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let sessionKey = ''
let ingestPausedUntil = 0
let lastFlushAt = 0

// --------- Public API -------------------------------------------------

export function installClientPerfReporter(options: ClientPerfOptions = {}): void {
  if (installed) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (options.disabled) return

  const defaultIngest = `${window.location.origin}/api/diagnostics/client-perf/`
  opts = {
    ingestUrl: options.ingestUrl ?? defaultIngest,
    tenant: options.tenant ?? _deriveTenant(),
    sampleRate: options.sampleRate ?? 1.0,
    maxBufferSize: options.maxBufferSize ?? 200,
    // Long idle debounce keeps the reporter off the "top pollers" list. The
    // tail is never lost: flush-on-hide (pagehide/visibilitychange) + the
    // localStorage-persisted buffer (restored next load) cover navigation.
    flushDebounceMs: options.flushDebounceMs ?? 30000,
    buildSha: options.buildSha,
    getAuthHeaders: options.getAuthHeaders,
    getOrganisationId: options.getOrganisationId,
    disabled: false,
  }

  sessionKey = getDiagnosticsSessionKey()
  _restoreBuffer()

  // Receive a timing for every completed fetch from the single fetch
  // wrapper in clientErrorReporter (success + error). We sample + record.
  setFetchTimingObserver((t) => {
    if (!_passSample()) return
    recordPerfSample({
      kind: 'fetch',
      name: `${t.method} ${t.urlTemplate}`,
      duration_ms: Math.round(t.durationMs),
      server_duration_ms: typeof t.serverTimingMs === 'number' ? Math.round(t.serverTimingMs) : undefined,
      route: _currentRoute(),
      // TRACING-LITE — carry the per-request correlation id the fetch
      // wrapper injected (same id the backend stamped onto its
      // ServerTimingSample + logs). Lets 0x3admin join a slow FE fetch
      // to the exact backend row + structured-log trail without OTEL.
      extra: t.correlationId ? { status: t.status, correlation_id: t.correlationId } : { status: t.status },
    })
  })

  _installWebVitals()
  _installRouteTiming()
  _installFlushOnHide()
  installed = true
}

/** Record one perf sample. Safe to call before install (no-op until then). */
export function recordPerfSample(sample: PerfSample): void {
  if (!installed) return
  if (!sample || typeof sample.duration_ms !== 'number' || !isFinite(sample.duration_ms)) return
  if (sample.duration_ms < 0) return
  buffer.push({
    ...sample,
    ts: new Date().toISOString(),
    session_key: sessionKey,
    tenant: opts.tenant,
    build_sha: opts.buildSha,
  })
  if (buffer.length > opts.maxBufferSize) {
    buffer.splice(0, buffer.length - opts.maxBufferSize)
  }
  _persist()
  // Burst path: a full batch has piled up — flush early so we don't drop
  // samples or sit on a stale buffer, but rate-limited so it can't POST-storm.
  if (buffer.length >= FLUSH_BATCH_SIZE && Date.now() - lastFlushAt >= MIN_BURST_FLUSH_MS) {
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    void _flushNow()
    return
  }
  _scheduleFlush()
}

/** Record a business KPI / product milestone (e.g.
 *  ``time_to_first_board_paint``, ``time_to_first_action``). ``valueMs``
 *  is the elapsed time in milliseconds. KPIs are ALWAYS kept (never
 *  sampled away) — they are low-volume, high-signal, one per milestone
 *  per session — and land as ``kind='kpi'`` rows that the 0x3admin
 *  Performance dashboard can chart as p50/p95 "time to X" alongside the
 *  technical render/fetch metrics. A vendor that knows its CPU usage to
 *  the millisecond but not that users wait 47s to find a button is
 *  measuring the wrong thing; this is the right thing. */
export function recordKpi(
  name: string,
  valueMs: number,
  extra?: Record<string, unknown>,
): void {
  if (typeof valueMs !== 'number' || !isFinite(valueMs) || valueMs < 0) return
  const rounded = Math.round(valueMs)
  recordPerfSample({
    kind: 'kpi',
    name,
    duration_ms: rounded,
    route: _currentRoute(),
    extra,
  })
  // Fan out to any registered sinks (e.g. the PostHog adapter) so a KPI
  // can ALSO land in a product-analytics tool without the call-site
  // knowing or caring which sinks exist. Sinks are best-effort: one that
  // throws must never break the primary 0x3admin recording above.
  for (const sink of _kpiSinks) {
    try {
      sink(name, rounded, extra)
    } catch {
      /* a sink must never break KPI recording */
    }
  }
}

// --------- KPI sinks (fan-out to extra destinations) ------------------
export type KpiSink = (
  name: string,
  valueMs: number,
  extra?: Record<string, unknown>,
) => void
const _kpiSinks = new Set<KpiSink>()

/** Register an extra destination for KPI events (called by recordKpi and
 *  recordKpiSince). Used by the PostHog adapter to forward product KPIs to
 *  PostHog in addition to the 0x3admin perf store. Returns an unsubscribe. */
export function addKpiSink(sink: KpiSink): () => void {
  _kpiSinks.add(sink)
  return () => {
    _kpiSinks.delete(sink)
  }
}

/** Remove a previously-registered KPI sink. */
export function removeKpiSink(sink: KpiSink): void {
  _kpiSinks.delete(sink)
}

// --------- KPI timers (start → since) ---------------------------------
// A tiny named-stopwatch registry so any flow can record a business
// "time to X" KPI without threading a start timestamp through props/refs:
//
//     markKpiStart('time_to_create_quote')      // when the form opens
//     ...user fills it in, submits, mutation succeeds...
//     recordKpiSince('time_to_create_quote', { org_id })   // onSuccess
//
// recordKpiSince computes the elapsed ms from the matching mark, emits it
// via recordKpi (kind='kpi'), and clears the mark so the next attempt
// starts clean. If there's no matching start (or it's stale) it no-ops —
// a KPI we can't anchor is worse than no KPI. Marks auto-expire after
// KPI_MARK_TTL_MS so an abandoned form (open then navigate away, come back
// hours later) can't emit an absurd multi-hour "time to create".
const _kpiMarks = new Map<string, number>()
const KPI_MARK_TTL_MS = 30 * 60_000 // 30 min — generous for a long form

function _now(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

/** Start (or restart) the stopwatch for a named business KPI. Call this at
 *  the natural START of a flow (form open / route mount / button click). */
export function markKpiStart(name: string): void {
  if (!name) return
  _kpiMarks.set(name, _now())
}

/** Clear a pending KPI mark without recording (e.g. the user cancelled). */
export function clearKpiStart(name: string): void {
  _kpiMarks.delete(name)
}

/** Record the elapsed time since the matching ``markKpiStart(name)`` as a
 *  ``kind='kpi'`` sample, then clear the mark. No-op if there's no live
 *  (non-expired) start — we never emit an unanchored or absurd duration.
 *  Returns the elapsed ms it recorded, or null if it no-op'd. */
export function recordKpiSince(
  name: string,
  extra?: Record<string, unknown>,
): number | null {
  const startedAt = _kpiMarks.get(name)
  if (typeof startedAt !== 'number') return null
  _kpiMarks.delete(name)
  const elapsed = _now() - startedAt
  if (!isFinite(elapsed) || elapsed < 0 || elapsed > KPI_MARK_TTL_MS) return null
  recordKpi(name, elapsed, extra)
  return Math.round(elapsed)
}

/** True when this sample should be kept under the configured sample rate.
 *  Renders/fetches use this; Web Vitals are always kept (1 per page). */
export function _passSample(): boolean {
  return opts.sampleRate >= 1.0 || Math.random() < opts.sampleRate
}

export function getPerfBuffer(): readonly QueuedSample[] {
  return buffer
}

export async function flushPerf(): Promise<number> {
  if (buffer.length === 0) return 0
  return _flushNow()
}

// --------- Internals --------------------------------------------------

function _deriveTenant(): string {
  try {
    const host = window.location.hostname // e.g. sam.quotech.ai
    const first = host.split('.')[0]
    if (!first || first === 'www' || first === 'localhost') return ''
    return first.slice(0, 32)
  } catch {
    return ''
  }
}

function _currentRoute(): string {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.hash}`
}

function _restoreBuffer(): void {
  try {
    const raw = localStorage.getItem(BUFFER_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        buffer = parsed.slice(-opts.maxBufferSize)
        _scheduleFlush()
      }
    }
  } catch {
    /* ignore */
  }
}

function _persist(): void {
  try {
    localStorage.setItem(BUFFER_STORAGE_KEY, JSON.stringify(buffer.slice(-opts.maxBufferSize)))
  } catch {
    /* quota/disabled — accept loss */
  }
}

function _scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void _flushNow()
  }, opts.flushDebounceMs)
}

async function _flushNow(extra?: { keepalive?: boolean }): Promise<number> {
  if (!opts.ingestUrl) return 0
  if (buffer.length === 0) return 0
  if (typeof window === 'undefined') return 0
  if (Date.now() < ingestPausedUntil) return 0
  // Stamp every attempt (success or fail) so the burst path stays rate-limited
  // even during a backend outage — telemetry must never amplify load.
  lastFlushAt = Date.now()

  const batch = buffer.slice(-100) // BE ingests up to 100 / request
  let authHeaders: Record<string, string> = {}
  if (opts.getAuthHeaders) {
    try {
      const v = await opts.getAuthHeaders()
      if (v && typeof v === 'object') authHeaders = v
    } catch {
      /* anonymous POST is fine */
    }
  }

  let organisationId: number | string | null | undefined
  if (opts.getOrganisationId) {
    try {
      organisationId = opts.getOrganisationId()
    } catch {
      /* ignore */
    }
  }

  const payload = {
    organisation_id: organisationId ?? undefined,
    events: batch,
  }

  try {
    const res = await fetch(opts.ingestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: extra?.keepalive ?? false,
    })
    if (res.ok || res.status === 202) {
      const n = batch.length
      buffer = buffer.slice(n)
      _persist()
      return n
    }
    if (res.status === 429) {
      ingestPausedUntil = Date.now() + 120_000
    }
    return 0
  } catch {
    return 0
  }
}

function _installFlushOnHide(): void {
  // pagehide / visibilitychange(hidden) is the reliable "tab going away"
  // signal on mobile + desktop; flush with keepalive so the last batch
  // (incl. the page's LCP/CLS) isn't lost.
  const flush = () => {
    void _flushNow({ keepalive: true })
  }
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
}

// ---- Route transitions -----------------------------------------------
// Framework-agnostic: patch the History API + popstate/hashchange so EVERY
// hub gets route timing for free (same philosophy as the single fetch
// wrapper), without depending on react-router or touching DashboardLayout.
// "Duration" = time from the navigation to the next paint (the perceived
// cost of swapping the route's UI in). Lazy-loaded routes that suspend will
// under-report (we measure to the fallback paint); that's an accepted v1
// limitation noted for the dashboard.
let _lastRoutePath = ''

// _now() is declared once above (shared with the KPI timers).

/** Collapse ids/uuids to placeholders so route names stay bounded-cardinality
 *  (the backend groups by name). e.g. /crm/jobs/123 → /crm/jobs/:id. Exported
 *  for tests. */
export function _templatiseRoute(path: string): string {
  if (!path) return '/'
  const out = path
    .split('/')
    .map((seg) => {
      if (!seg) return seg
      if (/^\d+$/.test(seg)) return ':id'
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}/i.test(seg)) return ':uuid'
      return seg
    })
    .join('/')
  return out || '/'
}

function _routePath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : ''
}

function _installRouteTiming(): void {
  if (typeof window === 'undefined' || typeof window.history === 'undefined') return
  _lastRoutePath = _routePath()

  const onChange = (): void => {
    const from = _lastRoutePath
    const to = _routePath()
    if (to === from) return
    _lastRoutePath = to
    const t0 = _now()
    const schedule =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => window.setTimeout(() => cb(_now()), 0)
    schedule(() => {
      if (!_passSample()) return
      recordPerfSample({
        kind: 'route',
        name: _templatiseRoute(to),
        duration_ms: Math.round(_now() - t0),
        route: to,
        extra: { from: _templatiseRoute(from) },
      })
    })
  }

  const wrap = (key: 'pushState' | 'replaceState'): void => {
    const orig = window.history[key] as (...a: unknown[]) => unknown
    if (typeof orig !== 'function') return
    window.history[key] = function (this: History, ...args: unknown[]) {
      const ret = orig.apply(this, args)
      try {
        onChange()
      } catch {
        /* perf timing must never break navigation */
      }
      return ret
    } as History[typeof key]
  }

  try {
    wrap('pushState')
    wrap('replaceState')
  } catch {
    /* SPA history unavailable — skip route timing */
  }
  window.addEventListener('popstate', onChange)
  window.addEventListener('hashchange', onChange)
}

// ---- Web Vitals (no external dep) ------------------------------------
function _installWebVitals(): void {
  if (typeof PerformanceObserver === 'undefined') return

  // LCP: keep the largest reported value.
  _observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1] as LcpEntry | undefined
    if (!last) return
    const value = last.renderTime || last.loadTime || last.startTime
    if (value) _stashVital('LCP', value)
  })

  // CLS: cumulative layout shift (sum of non-input shifts). Scaled x1000
  // so it shares the ms-typed duration column (0.1 CLS → 100).
  //
  // PERF-2026-07-09-CLS-ATTRIBUTION (load-speed audit R7): 62k CLS
  // samples/7d all read attribution "unknown" (median 0.35 — 'poor'),
  // so nothing could say WHAT shifts. We now carry the worst single
  // shift's target element (and its share of the total) in ``extra``
  // so the 0x3admin Performance dashboard can rank shifters and the
  // fix lands on the measured element instead of a guess.
  let clsValue = 0
  let worstShiftValue = 0
  let worstShiftTarget = ''
  _observe('layout-shift', (entries) => {
    for (const e of entries as LayoutShiftEntry[]) {
      if (e.hadRecentInput) continue
      clsValue += e.value
      if (e.value > worstShiftValue) {
        worstShiftValue = e.value
        worstShiftTarget = _describeShiftSource(e)
      }
    }
    _stashVital('CLS', clsValue * 1000, {
      largest_shift_target: worstShiftTarget,
      largest_shift_value: Math.round(worstShiftValue * 1000),
    })
  })

  // INP proxy: worst event-processing duration observed.
  let worstInp = 0
  _observe('event', (entries) => {
    for (const e of entries as PerformanceEventTiming[]) {
      const dur = e.duration || 0
      if (dur > worstInp) {
        worstInp = dur
        _stashVital('INP', worstInp)
      }
    }
  }, { durationThreshold: 40 })
}

/** Bounded, greppable description of the largest-shift node: tag + id +
 *  data-testid + first classes. Pure DOM reads, never throws — CLS
 *  attribution must not be able to break the page it measures.
 *  Exported (underscore convention) for tests only. */
export function _describeShiftSource(entry: LayoutShiftEntry): string {
  try {
    const node = entry.sources?.find((s) => s.node)?.node
    if (!node || node.nodeType !== 1) return 'unknown'
    const el = node as Element
    const tag = el.tagName.toLowerCase()
    const id = el.id ? `#${el.id}` : ''
    const testId = el.getAttribute('data-testid')
    const cls = (el.getAttribute('class') || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .join('.')
    return `${tag}${id}${testId ? `[${testId}]` : ''}${cls ? `.${cls}` : ''}`.slice(0, 140)
  } catch {
    return 'unknown'
  }
}

const _vitals: Record<string, number> = {}
function _stashVital(name: string, value: number, extra?: Record<string, unknown>): void {
  _vitals[name] = value
  // Vitals finalise on tab-hide; record the latest snapshot opportunistically
  // (deduped server-side by name+session if needed). One per flush window.
  recordPerfSample({
    kind: 'web_vital',
    name,
    duration_ms: Math.round(value),
    route: _currentRoute(),
    ...(extra ? { extra } : {}),
  })
}

function _observe(
  type: string,
  cb: (entries: PerformanceEntryList) => void,
  extraInit?: ObserveInit,
): void {
  try {
    const po = new PerformanceObserver((list) => cb(list.getEntries()))
    po.observe({ type, buffered: true, ...(extraInit || {}) } as ObserveInit)
  } catch {
    /* unsupported entry type — skip silently */
  }
}
